import type { Element } from '../game/elements';
import { elementLabel } from '../game/elements';
import type { CombatState } from '../game/types';

export const STATUS_IDS = [
  'stun',
  'freeze',
  'paralysis',
  'poison',
  'burn',
  'weaken',
  'invulnerable',
  'elemental_immunity',
  'fear',
  'power_up',
] as const;

export type StatusId = (typeof STATUS_IDS)[number];

export interface ActiveStatus {
  id: StatusId;
  /** Turns remaining; decremented after each full combat round. */
  remainingTurns: number;
  /** DOT damage, paralysis chance, weaken or power multiplier. */
  potency?: number;
  /** Required for elemental_immunity; optional for other statuses. */
  element?: Element;
}

function sameStatus(a: ActiveStatus, b: ActiveStatus): boolean {
  if (a.id !== b.id) {
    return false;
  }
  return a.id !== 'elemental_immunity' || a.element === b.element;
}

export function hasStatus(list: readonly ActiveStatus[], id: StatusId): boolean {
  return list.some((status) => status.id === id && status.remainingTurns > 0);
}

export function getStatus(
  list: readonly ActiveStatus[],
  id: StatusId,
): ActiveStatus | undefined {
  return list.find((status) => status.id === id && status.remainingTurns > 0);
}

export function hasElementalImmunity(
  list: readonly ActiveStatus[],
  element: Element,
): boolean {
  return list.some(
    (status) =>
      status.id === 'elemental_immunity' &&
      status.element === element &&
      status.remainingTurns > 0,
  );
}

/** Add/refresh a status. Same-element immunity stacks independently by element. */
export function applyStatus(
  list: readonly ActiveStatus[],
  status: ActiveStatus,
): ActiveStatus[] {
  const current = list.find((entry) => sameStatus(entry, status));
  const refreshed = current
    ? {
        ...current,
        ...status,
        remainingTurns: Math.max(current.remainingTurns, status.remainingTurns),
      }
    : status;
  return [...list.filter((entry) => !sameStatus(entry, status)), refreshed];
}

export function tickStatuses(list: readonly ActiveStatus[]): ActiveStatus[] {
  return list
    .map((status) => ({ ...status, remainingTurns: status.remainingTurns - 1 }))
    .filter((status) => status.remainingTurns > 0);
}

export function statusLabel(id: StatusId): string {
  switch (id) {
    case 'stun':
      return 'Choáng';
    case 'freeze':
      return 'Đóng băng';
    case 'paralysis':
      return 'Tê liệt';
    case 'poison':
      return 'Độc';
    case 'burn':
      return 'Thiêu đốt';
    case 'weaken':
      return 'Suy yếu';
    case 'invulnerable':
      return 'Bất tử';
    case 'elemental_immunity':
      return 'Miễn nhiễm';
    case 'fear':
      return 'Hoảng sợ';
    case 'power_up':
      return 'Cường hóa';
  }
}

export function activeStatusLabel(status: ActiveStatus): string {
  return status.id === 'elemental_immunity' && status.element
    ? `${statusLabel(status.id)} ${elementLabel(status.element)}`
    : statusLabel(status.id);
}

export function activeStatusDescription(status: ActiveStatus): string {
  switch (status.id) {
    case 'stun':
    case 'freeze':
      return 'Chắc chắn mất hành động';
    case 'paralysis':
      return `${Math.round((status.potency ?? 0.5) * 100)}% khả năng mất hành động mỗi hiệp`;
    case 'poison':
      return `Nhận ${status.potency ?? 4} sát thương Độc mỗi hiệp`;
    case 'burn':
      return `Nhận ${status.potency ?? 4} sát thương Thiêu đốt mỗi hiệp`;
    case 'weaken':
      return `Sát thương phải nhận ×${status.potency ?? 1.35}`;
    case 'invulnerable':
      return 'Chặn toàn bộ sát thương';
    case 'elemental_immunity':
      return `Chặn toàn bộ sát thương ${status.element ? elementLabel(status.element) : 'nguyên tố'}`;
    case 'fear':
      return 'Không thể chạy trốn';
    case 'power_up':
      return `Sát thương đòn trúng kế tiếp ×${status.potency ?? 1.4}`;
  }
}

export type ActionBlockReason = 'stun' | 'freeze' | 'paralysis';

/** Stun/freeze always block; paralysis rolls against potency (default 50%). */
export function rollActionBlock(
  statuses: readonly ActiveStatus[],
  rng: () => number = Math.random,
): ActionBlockReason | null {
  if (getStatus(statuses, 'stun')) return 'stun';
  if (getStatus(statuses, 'freeze')) return 'freeze';
  const paralysis = getStatus(statuses, 'paralysis');
  return paralysis && rng() < (paralysis.potency ?? 0.5) ? 'paralysis' : null;
}

export function actionBlockLabel(reason: ActionBlockReason): string {
  switch (reason) {
    case 'stun':
      return 'bị choáng';
    case 'freeze':
      return 'bị đóng băng';
    case 'paralysis':
      return 'bị tê liệt';
  }
}

/** Poison and burn independently damage the enemy before its action. */
export function applyEnemyDamageOverTime(
  combat: CombatState,
): { combat: CombatState; defeated: boolean; damage: number } {
  let next = combat;
  const hpBefore = combat.enemyHp;
  const effects = [
    { id: 'poison' as const, icon: '☠️', label: 'Độc', fallback: 4 },
    { id: 'burn' as const, icon: '🔥', label: 'Thiêu đốt', fallback: 4 },
  ];

  for (const effect of effects) {
    const status = getStatus(next.enemyStatuses, effect.id);
    if (!status || next.enemyHp <= 0) continue;
    const damage = status.potency ?? effect.fallback;
    next = {
      ...next,
      enemyHp: Math.max(0, next.enemyHp - damage),
      log: [`${effect.icon} ${effect.label} gây ${damage} sát thương.`, ...next.log].slice(0, 8),
    };
  }

  if (next.enemyHp <= 0) {
    next = {
      ...next,
      log: ['Kẻ địch gục vì sát thương trạng thái.', ...next.log].slice(0, 8),
    };
  }
  return {
    combat: next,
    defeated: next.enemyHp <= 0,
    damage: Math.max(0, hpBefore - next.enemyHp),
  };
}
